import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import isEqual from 'lodash.isequal';

import { AppState } from 'services/types';
import {
    VirtualizedTableColumnKey,
    VirtualizedTableColumnWidth,
    VirtualizedTableColumns,
    VirtualizedTableItem
} from 'services/virtualizedTable/types';
import {
    requestedCreateActiveIngredient,
    requestedUpdateActiveIngredient,
    requestedDeleteActiveIngredient
} from 'services/activeIngredients/actions';
import { setColumnWidth } from 'services/columnWidths/actions';
import VirtualizedTable from 'components/VirtualizedTable';

import {
    selectActiveIngredientsTableColumns,
    makeSelectActiveIngredientItems,
    selectIsWritable
} from './selectors';

interface OwnProps {
    tableId: string;
}

interface ConnectedProps {
    items: VirtualizedTableItem[];
    columns: VirtualizedTableColumns;
    isWritable: ReturnType<typeof selectIsWritable>;
}

interface DispatchProps {
    setColumnWidth: typeof setColumnWidth;
    requestedCreateActiveIngredient: typeof requestedCreateActiveIngredient;
    requestedUpdateActiveIngredient: typeof requestedUpdateActiveIngredient;
    requestedDeleteActiveIngredient: typeof requestedDeleteActiveIngredient;
}

type Props = OwnProps & ConnectedProps & DispatchProps;

export class ActiveIngredientsVirtualizedTable extends React.Component<Props> {
    shouldComponentUpdate(nextProps: Props): boolean {
        return !isEqual(this.props, nextProps);
    }

    handleColumnResized = (
        columnKey: VirtualizedTableColumnKey,
        columnWidth: VirtualizedTableColumnWidth
    ): void => {
        const { tableId, ...props } = this.props;
        props.setColumnWidth(tableId, columnKey, columnWidth);
    };

    handleCreate = (item: VirtualizedTableItem): void => {
        this.props.requestedCreateActiveIngredient(item);
    };

    handleDelete = (items: VirtualizedTableItem[]): void => {
        if (items[0] && items[0].id) {
            this.props.requestedDeleteActiveIngredient(items[0].id);
        }
    };

    handleItemUpdate = (item: VirtualizedTableItem): void => {
        const { name, externalId } = item;

        this.props.requestedUpdateActiveIngredient(item.id, {
            name,
            externalId
        });
    };

    renderVirtualizedTable = ({ height, width }: Size): React.ReactElement => {
        const { tableId, items, columns, isWritable } = this.props;

        return (
            <VirtualizedTable
                id={tableId}
                height={height}
                width={width}
                columns={columns}
                items={items}
                onColumnResized={this.handleColumnResized}
                label="Active Ingredient"
                labelPlural="Active Ingredients"
                editable={isWritable}
                enableCreating={isWritable}
                enableItemEditing={isWritable}
                enableDeleting={isWritable}
                selectable={isWritable}
                onCreate={this.handleCreate}
                onItemUpdate={this.handleItemUpdate}
                onDelete={this.handleDelete}
            />
        );
    };

    render(): React.ReactElement {
        return (
            <AutoSizer>
                {(size: Size): React.ReactElement =>
                    this.renderVirtualizedTable(size)
                }
            </AutoSizer>
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    items: makeSelectActiveIngredientItems(),
    columns: selectActiveIngredientsTableColumns,
    isWritable: selectIsWritable
});

export default connect(mapStateToProps, {
    setColumnWidth,
    requestedCreateActiveIngredient,
    requestedUpdateActiveIngredient,
    requestedDeleteActiveIngredient
})(ActiveIngredientsVirtualizedTable);
